import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import type { Network } from '../../api/types';
import { vt } from '../../theme';
import { freeAddresses, usage } from './model';

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '14px',
    margin: 0,
    padding: 0,
    listStyleType: 'none',
    flexShrink: 0,
  },
  card: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    padding: '16px 18px 14px',
    borderRadius: '10px',
    border: `1px solid transparent`,
    backgroundColor: vt.surface3,
    color: 'inherit',
    fontFamily: 'inherit',
    textAlign: 'start',
    cursor: 'pointer',
    ':hover': { border: `1px solid ${vt.surface2}` },
    ':focus-visible': { outline: `2px solid ${vt.brand}`, outlineOffset: '1px' },
  },
  selected: {
    backgroundColor: vt.brandTint,
    border: `1px solid ${vt.brand}`,
    ':hover': { border: `1px solid ${vt.brand}` },
  },
  top: { display: 'flex', alignItems: 'baseline', columnGap: '12px', minWidth: 0 },
  name: { fontSize: '16px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1, overflowWrap: 'anywhere' },
  cidr: { fontFamily: tokens.fontFamilyMonospace, fontSize: '12px', color: vt.fg3, flexGrow: 1 },
  chip: {
    height: '18px',
    lineHeight: '18px',
    paddingInline: '8px',
    borderRadius: tokens.borderRadiusCircular,
    fontSize: '10px',
    color: vt.orange,
    backgroundColor: vt.tintOrange,
    flexShrink: 0,
  },
  note: {
    marginTop: '6px',
    minHeight: '15px',
    fontSize: '11px',
    color: vt.fg3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rule: { height: '1px', backgroundColor: vt.surface2, margin: '12px -17px 10px' },
  counts: { display: 'flex', alignItems: 'baseline', columnGap: '28px' },
  count: { display: 'inline-flex', alignItems: 'baseline', columnGap: '6px', fontSize: '11px', color: vt.fg3 },
  n: { fontFamily: tokens.fontFamilyMonospace, fontSize: '16px', color: vt.fg1 },
  nFree: { color: vt.fg2 },
  track: { marginTop: '12px', height: '4px', borderRadius: '2px', backgroundColor: vt.surface2, overflow: 'hidden' },
  fill: { height: '100%', borderRadius: '2px', backgroundColor: vt.green },
  fillIsolated: { backgroundColor: vt.fg3 },
});

/** One card per network: name, CIDR, isolated chip, note, devices, free addresses and a usage bar. */
export function NetworkCards({
  networks,
  selectedId,
  onSelect,
}: {
  networks: readonly Network[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const s = useStyles();
  return (
    <ul className={s.grid} aria-label="Network cards">
      {networks.map((n) => {
        const free = freeAddresses(n);
        return (
          <li key={n.id} data-network={n.id}>
            <button
              type="button"
              className={mergeClasses(s.card, n.id === selectedId && s.selected)}
              aria-pressed={n.id === selectedId}
              aria-label={`${n.name} ${n.cidr}`}
              onClick={() => onSelect(n.id)}
            >
              <span className={s.top}>
                <span className={s.name}>{n.name}</span>
                <span className={s.cidr}>{n.cidr}</span>
                {n.isolated && <span className={s.chip}>isolated</span>}
              </span>
              <span className={s.note}>{n.note ?? ''}</span>
              <span className={s.rule} aria-hidden />
              <span className={s.counts}>
                <span className={s.count} data-count="devices">
                  <span className={s.n}>{n.deviceCount}</span>
                  {n.deviceCount === 1 ? 'device' : 'devices'}
                </span>
                <span className={s.count} data-count="free">
                  <span className={mergeClasses(s.n, s.nFree)}>{free}</span>
                  free
                </span>
              </span>
              <span
                className={s.track}
                role="meter"
                aria-label={`${n.name} usage`}
                aria-valuemin={0}
                aria-valuemax={n.deviceCount + free}
                aria-valuenow={n.deviceCount}
              >
                <span
                  className={mergeClasses(s.fill, n.isolated && s.fillIsolated)}
                  style={{ display: 'block', width: `${usage(n) * 100}%` }}
                />
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
